import { type ApolloError, useMutation } from '@apollo/client';
import { buildDealPromptAudienceInsertInput } from '@openx/components-deal-prompt/lib/utils/buildDealPromptAudienceInsertInput';
import type { DealPromptAudienceCreateDealSubmitPayload } from '@openx/components-deal-prompt/lib/utils/types';
import { TargetingFields } from '@openx/types/targeting/targeting';
import { Intersect } from '@openx/types/targeting/targetingValuesTypes';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAuth } from '../../../components/AuthContext/context';
import {
  ActivateAudienceDocument,
  type ActivateAudienceMutation,
  type ActivateAudienceMutationVariables,
} from '../../../graphql/mutation/audiences/ActivateAudience.generated';
import {
  CreateAudienceDocument,
  type CreateAudienceMutation,
  type CreateAudienceMutationVariables,
} from '../../../graphql/mutation/audiences/CreateAudience.generated';
import { CreateLogDocument } from '../../../graphql/mutation/error/createLog.generated';
import {
  isAudienceNameConflictError,
  isAudienceNamePath,
} from '../../../modules/audiences/AudienceForm/utils/isAudienceNameConflictError';
import { NestedErrorCode } from '../../../modules/graphql/types';
import { useLocalErrorHandling } from '../../../modules/graphql/useLocalErrorHandling';

export function useDealPromptAudienceCreateDealSubmit() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { session } = useAuth();
  const [createAudience] = useMutation<CreateAudienceMutation, CreateAudienceMutationVariables>(
    CreateAudienceDocument,
    {
      refetchQueries: ['GetAudiencesList', 'GetAudienceTemplates'],
    }
  );
  const [activateAudience] = useMutation<ActivateAudienceMutation, ActivateAudienceMutationVariables>(
    ActivateAudienceDocument,
    {
      refetchQueries: ['GetAudiencesList', 'GetAudienceTemplates'],
    }
  );

  const [pushLog] = useMutation(CreateLogDocument);

  useLocalErrorHandling(
    CreateAudienceDocument,
    useCallback(
      error => {
        if (error.code !== NestedErrorCode.JOINT_UNIQUENESS_VIOLATION || !isAudienceNamePath(error.path)) return false;
        enqueueSnackbar(t('Audience with such name already exists'), { variant: 'error' });
        return true;
      },
      [enqueueSnackbar, t]
    )
  );

  return useCallback(
    async ({
      form,
      segmentSet,
      selectedSegments,
      audienceAccountIdOverride,
      dealFormAccountUidOverride,
    }: DealPromptAudienceCreateDealSubmitPayload) => {
      if (selectedSegments.length === 0) {
        enqueueSnackbar(t('This set has no segments to add'), { variant: 'error' });
        throw new Error('Deal Prompt audience create: empty segment selection');
      }

      const userId = session?.id;
      const accountId = audienceAccountIdOverride ?? session?.account.id;
      const object = buildDealPromptAudienceInsertInput({
        userId,
        accountId,
        form,
        segmentSet,
        selectedSegments,
      });

      try {
        const { data } = await createAudience({ variables: { object } });
        const audienceId = data?.insert_audience_one?.id;

        if (!audienceId) {
          enqueueSnackbar(t('Failed to create audience'), { variant: 'error' });

          throw new Error('Deal Prompt audience create: missing audience id');
        }

        await activateAudience({ variables: { id: audienceId } });

        const dsInitialDealFormPreset = {
          targeting: {
            audience: {
              openaudience_custom: {
                op: Intersect.INTERSECTS,
                val: `openaudience-${audienceId}`,
              },
            },
          },
        };

        const accountUid = dealFormAccountUidOverride ?? session?.exchange_session?.user.account_uid;

        navigate('/deals/create', {
          state: {
            audienceId,
            dsInitialDealFormPreset,
            ...(accountUid ? { accountUid } : {}),
            useAsTemplate: true,
            fromScreenInfoConfig: {
              message: t('From Deal Prompt'),
              messageFieldNames: [TargetingFields.AUDIENCE],
              messageTitleName: t('Create Deal'),
            },
          },
        });
      } catch (error) {
        const apolloError = error as ApolloError;
        const { message } = apolloError;
        const isJointUniquenessViolation = isAudienceNameConflictError(apolloError);

        try {
          await pushLog({
            variables: {
              location: window.location.href,
              msg: message || 'Error when creating Audience',
              name: session?.exchange_session?.user.name,
              email: session?.exchange_session?.user.email,
            },
          });
        } catch {
          throw error;
        }

        if (!isJointUniquenessViolation && message !== 'Joint uniqueness violation') {
          enqueueSnackbar(t(message), { variant: 'error' });
        }

        if (isJointUniquenessViolation) {
          throw error;
        }
      }
    },
    [
      activateAudience,
      createAudience,
      enqueueSnackbar,
      navigate,
      pushLog,
      session?.account.id,
      session?.exchange_session?.user.account_uid,
      session?.id,
      t,
      session?.exchange_session?.user.email,
      session?.exchange_session?.user.name,
    ]
  );
}
